import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Search, TrendingUp, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import AppUnavailable from "@/pages/AppUnavailable";

export default function KBSearchInsights() {
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);
  const [days, setDays] = usePersistedState("kb-search-insights.days", 30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/kb/search/insights", { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, [days]);

  if (denied) return <AppUnavailable appName="KB" inactive={false} />;

  return (
    <div className="mx-auto max-w-4xl" data-testid="kb-search-insights-page">
      <button onClick={() => navigate("/kb")} data-testid="kb-insights-back"
        className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100">
        <ArrowLeft className="h-4 w-4" /> Back to Knowledge Base
      </button>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Search className="h-5 w-5 text-primary" />
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-slate-100">Search Insights</h1>
        </div>
        <div className="flex gap-1" data-testid="kb-insights-range">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`kb-insights-days-${d}`}
              className={`rounded-md border px-3 py-1 text-xs font-medium ${days === d ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 text-center" data-testid="kb-insights-total">
              <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">{data.total_searches.toLocaleString()}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">searches (last {data.days} days)</div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center" data-testid="kb-insights-zero">
              <div className="text-3xl font-bold text-amber-700">{data.zero_result_searches.toLocaleString()}</div>
              <div className="text-xs text-amber-700">returned nothing</div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="kb-insights-top">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100">
                <TrendingUp className="h-4 w-4 text-emerald-500" /> Most searched
              </h2>
              {data.top_queries.length === 0 ? <p className="text-sm text-slate-400 dark:text-slate-500">No searches yet.</p> : (
                <ul className="space-y-1.5">
                  {data.top_queries.map((r, i) => (
                    <li key={i} className="flex items-center justify-between text-sm">
                      <span className="truncate text-slate-700 dark:text-slate-200">{r.query}</span>
                      <span className="ml-2 shrink-0 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-xs text-slate-500 dark:text-slate-400">{r.searches}×</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="kb-insights-gaps">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100">
                <AlertCircle className="h-4 w-4 text-rose-500" /> Knowledge gaps (0 results)
              </h2>
              {data.knowledge_gaps.length === 0 ? <p className="text-sm text-slate-400 dark:text-slate-500">No unanswered searches. 🎉</p> : (
                <ul className="space-y-1.5">
                  {data.knowledge_gaps.map((r, i) => (
                    <li key={i} className="flex items-center justify-between text-sm">
                      <span className="truncate text-rose-700">{r.query}</span>
                      <span className="ml-2 shrink-0 rounded bg-rose-100 px-1.5 py-0.5 text-xs text-rose-600">{r.searches}×</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
